/* eslint-disable import/prefer-default-export */
import * as R from 'ramda';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { defineMessage, MessageDescriptor } from 'react-intl';
import { requestUpload, rewriteAppUrl } from 'utils/api';
import num from 'numeral';
import config from 'config';
import { RcFile } from 'antd/es/upload/interface';
import { SortOrder } from 'antd/lib/table/interface';
import { formatXLSName } from 'utils/files';
import { convertImportExportStatusCodeToJobStatus } from 'Common/utils';
import {
  Category,
  Attribute,
  ProductDetailsFormPayload,
  SavingProduct,
  SavingVariation,
  NormalizedProductVariation,
  ProductsImportHistoryRecord,
  ProductsImportTableRecord,
  ProductsImportType,
  StocktakingImportJob,
  StocktakingImportTableRecord,
  GiftcardsImportHistoryRecord,
  GiftcardsImportTableRecord,
  ProductType,
} from 'utils/apiClient/services/inventory/types';

export type ProdType = 'single' | 'prod_with_vars';

dayjs.extend(utc);

export const getAttrString = R.pipe(
  R.sortBy<Attribute>(R.prop('key')),
  R.pluck('value'),
  R.join(', ')
);

export const getAttributeSetsFromVariations = R.pipe<
  NormalizedProductVariation[],
  Attribute[][],
  Attribute[],
  { [key: string]: { key: string; value: string }[] },
  { [key: string]: { key: string; value: string[] } },
  { key: string; value: string[] }[]
>(
  R.pluck('attributes'),
  R.flatten,
  R.groupBy(R.prop('key')),
  R.mapObjIndexed((v, key) => ({
    key,
    value: R.uniq(R.pluck('value', v)),
  })),
  R.values
);

const calculateNetPriceFromGross = (
  gross: string | undefined,
  tax_rate: string
) =>
  // gross / ( 1 + ( tax / 100 ) )
  // use numeral module for safe money calculation
  R.isNil(gross)
    ? '0'
    : num(gross)
        .divide(num(1).add(num(tax_rate).divide(100).value()).value())
        .format('0[.]0000000000');
const defaultToZero = R.defaultTo('0');

type AttrIn = { key: string; value: any };
type AttrOut = { [key: string]: string[] };

const transformAttributes = R.pipe<
  AttrIn[],
  AttrIn[],
  AttrOut[],
  { [key: string]: any }
>(
  R.filter((a: { key: string; value: any }) => !!a.key && !!a.value),
  R.map((a) => ({ [a.key]: [a.value] })),
  R.mergeAll
);

const cleanImage = R.pickAll(['id', 'new']);

export const mapCommonFields = (
  v: Pick<
    ProductDetailsFormPayload,
    | 'gross_price'
    | 'selling_price'
    | 'tax_rate'
    | 'quantity'
    | 'purchase_price'
    | 'attributes'
    | 'images'
  >
) => {
  const gross_price = v.gross_price ?? v.selling_price;

  return {
    price: calculateNetPriceFromGross(
      gross_price === '' ? '0' : gross_price,
      v.tax_rate
    ),
    attributes: transformAttributes(v.attributes || []),
    images: v.images.filter((i) => i.status === 'done').map(cleanImage),
    quantity: defaultToZero(v.quantity),
    purchase_price: defaultToZero(v.purchase_price),
  };
};

export const transformToCoreApiSchema = R.pipe(
  (v: ProductDetailsFormPayload) => ({
    ...v,
    ...mapCommonFields(v),
    custom_price: v.custom_price === 'flexible',
    categories: v.categories.map((id) => ({ id })),
    variations: v.variations?.map((vx, index) => {
      const varData: SavingVariation = {
        ...vx,
        ...mapCommonFields(vx),
        purchase_price: vx.purchase_price ?? '0',
        quantity: vx.quantity ?? '0',
      };

      if (v.id !== undefined && varData.id) {
        // it's not creating the var, the id exists already
        return R.omit<SavingVariation, 'quantity'>(['quantity'], varData);
      }
      return varData;
    }),
  }),
  (v: SavingProduct) => {
    if (v.prod_type === 'prod_with_vars') {
      return R.pick(
        [
          'id',
          'name',
          'brand',
          'supplier',
          'tax_type',
          'description',
          'categories',
          'custom_price',
          'variations',
          'type',
          'ledger_account',
        ],
        v
      );
    }
    return R.omit(['new_cat', 'tax_rate', 'variations'], v);
  }
);

export const prodTypes: Array<{
  value: ProdType;
  title: MessageDescriptor;
  info: MessageDescriptor;
}> = [
  {
    value: 'single',
    title: defineMessage({
      defaultMessage: 'Single product',
    }),
    info: defineMessage({
      defaultMessage: 'A single product is the standard item (eg.: a cup)',
    }),
  },
  {
    value: 'prod_with_vars',
    title: defineMessage({
      defaultMessage: 'Product with variations',
    }),
    info: defineMessage({
      defaultMessage:
        'A product with variations contains multiple items in different modifications (eg.: shoes in different sizes and colors)',
    }),
  },
];

export const mapProductsImportHistoryToTable = R.map<
  ProductsImportHistoryRecord,
  ProductsImportTableRecord
>((item) => ({
  id: `#${item.id}`,
  num_imported: item.num_imported,
  time_added: dayjs(item.time_added, 'DD/MM/YYYY, HH:mm').toISOString(),
  filename: item.source_file.includes('https:')
    ? formatXLSName(item.source_file)
    : item.source_file,
  method: item.with_drop ? ProductsImportType.Replace : ProductsImportType.Add,
  errors: item.status_details?.fields,
  status: convertImportExportStatusCodeToJobStatus(item.status_code),
}));

export const mapGiftcardsImportHistoryToTable = R.map<
  GiftcardsImportHistoryRecord,
  GiftcardsImportTableRecord
>((item) => ({
  id: `#${item.id}`,
  num_imported: item.status_details?.num_imported ?? item.num_imported ?? 0,
  time_added: dayjs.utc(item.time_added).format('DD.MM.YYYY, HH:mm'),
  filename: item.source_file.includes('https:')
    ? formatXLSName(item.source_file)
    : item.source_file,
  errors: item.status_details?.fields,
  status: convertImportExportStatusCodeToJobStatus(item.status_code),
}));

export const groupImportErrorsByMessage = R.invert;
export const mapImportErrorsToDetailsTableRecord = R.compose<
  Record<string, string>,
  Record<string, string[]>,
  Array<[string, string[]]>,
  { message: string; cells: string[] }[]
>(
  R.map(([message, cells]) => ({ message, cells })),
  R.toPairs,
  R.invert
);

const getCategoryDetails = (c: Category) => ({
  ...R.pick(
    ['name', 'id', 'source', 'products_count', 'image', 'ledger_account'],
    c
  ),
  image: !c.image
    ? null
    : {
        ...c.image,
        urls: R.mapObjIndexed(rewriteAppUrl, c.image.urls),
      },
});

export const mapCategoryRawToCategoryTableRecord = R.map(getCategoryDetails);

export const mapStocktakingImportJobToTableRecord = R.map<
  StocktakingImportJob,
  StocktakingImportTableRecord
>((j) => ({
  id: `#${j.id}`,
  state: j.state,
  time_added: j.time_added,
  errors: j.result?.error?.fields ?? null,
}));

export const isProduct = (itemType: ProductType) =>
  itemType === ProductType.Product;

export const uploadTemplate = async (
  userToken: string,
  file: RcFile | null
) => {
  const url = `${config.CORE_URL}/api/products/stocktaking/import/`;
  await requestUpload({
    url,
    userToken,
    requestData: { file },
  });
};

export const validateLedgerAccountLength = (input: number) =>
  input === 4 ? 4 : 6;

export const getCurrentMonthYear = () => {
  const currentDate = new Date();
  const currentYear = String(currentDate.getFullYear());
  const currentMonth = String(currentDate.getMonth());
  return `${currentMonth}-${currentYear}`;
};

export const getCurrentYear = () => new Date().getFullYear();

export const filterUndefined: any = R.pipe(
  R.reject(R.isNil),
  R.map((item) => (Array.isArray(item) ? filterUndefined(item) : item))
);
export const getTableParams = (initialFilters = {}, pagination?: any) => ({
  pagination:
    pagination === undefined
      ? {
          current: 1,
          pageSize: 25,
        }
      : pagination,
  filters: initialFilters,
  sorter: { columnKey: 'name', order: 'ascend' as SortOrder },
  search: '',
  selectedItemsIdsByTable: [],
  customFilters: {},
});

export const fetchFilters = (itemType: ProductType) => {
  const filters = {
    [ProductType.Product]: { type: 'product' },
    [ProductType.Service]: { type: 'service' },
    [ProductType.Giftcard]: { is_giftcard: true },
  };

  return filters[itemType] || { is_giftcard: true };
};
